"use memo"

import {
  AutopilotTaskDto,
  TaskCategoryStage,
  TaskCategoryWorkflowType,
  useAssignTaskToUser,
  useGetAutopilotTasksForCampaign,
  useInvalidateUseGetAutopilotTasksForCampaign,
  useToggleTaskSteps,
} from '@urbanx/agx.activities.client'
import {
  AgxAnimatedToggleLoader,
  AgxAnimatedToggleLoaderState,
  AgxBadge,
  AgxDivider,
  AgxSpacer,
  AgxTab,
  AgxTypography,
  Icon_Chevron,
} from '@urbanx/agx.ui-components2'
import {
  useEffect,
  useRef,
  useState,
  ReactNode,
  Fragment,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  getDefaultSelectedWorkflowStage,
  getNextInProgressWorkflowStage,
  getWorkflowBadges,
  type WorkflowStageIndicator,
  WorkflowStageIndicatorStatus,
  sortTasksByCompletionThenOrder,
  TaskTabs,
} from 'utils/tasks'
import { applyTaskUpdateToTasks } from 'utils/tasks/taskUpdates'
import { useGetAssigneeOptions } from 'pages/allTasks/TaskFilters/useGetAssigneeOptions'
import Panel from 'components/panel/Panel'
import TaskCollapsible from './TaskCollapsible'
import { TaskBlocker } from 'pages/allTasks/TaskBlockers'
import './Tasks.scss'
import { isLastIncompleteTaskInStage } from './taskStepConfirmation'

export { TaskTabs } from 'utils/tasks'

export interface TasksProps {
  campaignId: string
  contractId?: string
  primaryWorkflowType: TaskCategoryWorkflowType
  // Filter function to exclude modal tasks
  taskFilter?: (task: AutopilotTaskDto) => boolean
  // Render prop for modal tasks
  renderAdditionalContent?: (params: {
    selectedTab: TaskTabs
    selectedWorkflowStage?: TaskCategoryStage
    allTasksCompleted: boolean
    tasks: AutopilotTaskDto[]
    isViewingCompletedStage: boolean
  }) => ReactNode
  onTasksChange?: (tasks: AutopilotTaskDto[]) => void
}

export interface TasksHandle {
  invalidateTasks: () => void
  tasks: AutopilotTaskDto[]
  allTasksCompleted: boolean
}

type CompletedStageView = {
  stage: TaskCategoryStage
  inProgressWorkflowStage?: TaskCategoryStage
}

const filterTasksForStage = (
  tasks: AutopilotTaskDto[],
  stage?: TaskCategoryStage
) =>
  tasks
    .filter(
      (task) =>
        task.category?.stage === stage && (task.isCompleted || !task.isLocked)
    )
    .sort(sortTasksByCompletionThenOrder)

const Tasks = ({
  campaignId,
  contractId,
  primaryWorkflowType,
  taskFilter,
  renderAdditionalContent,
  onTasksChange,
}: TasksProps) => {
  const { hash } = useLocation()
  const navigate = useNavigate()
  const selectedTab =
    (new URLSearchParams(hash.replace(/^#/, '')).get('tab') as TaskTabs) ??
    TaskTabs.Tasks

  const [completedStageView, setCompletedStageView] = useState<
    CompletedStageView | undefined
  >()

  const queryClient = useQueryClient()
  const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign()
  const { assigneeOptions } = useGetAssigneeOptions()

  const { assignTaskToUser } = useAssignTaskToUser({
    onSuccess: () => {
      invalidateTasks(campaignId, contractId)
    },
    onError: () => {},
  })

  const { toggleTaskSteps } = useToggleTaskSteps({
    onSuccess: (updatedTask) => {
      if (!updatedTask) return

      queryClient.setQueryData<AutopilotTaskDto[]>(
        ['getAutopilotTasksForCampaign', campaignId, contractId],
        (previousTasks) =>
          previousTasks
            ? applyTaskUpdateToTasks(previousTasks, { task: updatedTask })
            : previousTasks
      )
    },
    onError: () => {},
  })

  const { data: autoPilotTasks } = useGetAutopilotTasksForCampaign(
    campaignId,
    contractId,
    !!campaignId && (contractId ? !!contractId : true)
  )

  const tasks = autoPilotTasks ?? []

  const primaryTasks = tasks.filter(
    (task) => task.category?.workflowType === primaryWorkflowType
  )

  const allTasksCompleted =
    primaryTasks.length > 0 && primaryTasks.every((task) => task.isCompleted)

  const workflowBadges = getWorkflowBadges(
    primaryTasks as Required<AutopilotTaskDto>[]
  )
  const inProgressWorkflowStage = getNextInProgressWorkflowStage(workflowBadges)
  const defaultSelectedWorkflowStage =
    getDefaultSelectedWorkflowStage(workflowBadges)
  const isViewingCompletedStage = Boolean(
    completedStageView &&
      completedStageView.inProgressWorkflowStage === inProgressWorkflowStage &&
      workflowBadges.some(
        (badge) =>
          badge.stage === completedStageView.stage &&
          badge.status === WorkflowStageIndicatorStatus.COMPLETED
      )
  )

  let selectedWorkflowStage = defaultSelectedWorkflowStage
  if (isViewingCompletedStage) {
    selectedWorkflowStage = completedStageView?.stage
  }

  const depositTasks = tasks
    .filter(
      (task) =>
        task.category?.stage === TaskCategoryStage.Deposits &&
        task.category?.workflowType !== primaryWorkflowType
    )
    .sort(sortTasksByCompletionThenOrder)

  const otherTasks = tasks
    .filter(
      (task) =>
        (task.category?.workflowType ===
          TaskCategoryWorkflowType.IndependentCampaign ||
          task.category?.workflowType ===
            TaskCategoryWorkflowType.IndependentContract) &&
        task.category?.stage !== TaskCategoryStage.Deposits
    )
    .sort(sortTasksByCompletionThenOrder)

  useEffect(() => {
    if (!campaignId) return
    if (contractId !== undefined && !contractId) return
    invalidateTasks(campaignId, contractId)
  }, [campaignId, contractId, invalidateTasks])

  // Keep a stable ref so the effect doesn't re-run when the parent passes
  // a new (non-memoized) callback reference on each render.
  const onTasksChangeRef = useRef(onTasksChange)
  onTasksChangeRef.current = onTasksChange

  useEffect(() => {
    if (autoPilotTasks) {
      onTasksChangeRef.current?.(autoPilotTasks)
    }
  }, [autoPilotTasks])

  const handleBadgeClick = (badge: WorkflowStageIndicator) => {
    if (badge.status === WorkflowStageIndicatorStatus.LOCKED || !badge.stage)
      return

    if (badge.status === WorkflowStageIndicatorStatus.COMPLETED) {
      setCompletedStageView({
        stage: badge.stage,
        inProgressWorkflowStage,
      })
      return
    }

    setCompletedStageView(undefined)
  }

  const filteredTasksForStage = filterTasksForStage(
    primaryTasks,
    selectedWorkflowStage
  )
  const displayTasks = taskFilter
    ? filteredTasksForStage.filter(taskFilter)
    : filteredTasksForStage

  const depositIncompleteCount = depositTasks.filter(
    (x) => !x.isCompleted
  ).length

  const otherIncompleteCount = otherTasks.filter((x) => !x.isCompleted).length

  const tabOptions = [
    {
      id: TaskTabs.Tasks,
      label: TaskTabs.Tasks,
      selected: selectedTab === TaskTabs.Tasks,
    },
    ...(depositTasks.length > 0
      ? [
          {
            id: TaskTabs.Deposits,
            label: TaskTabs.Deposits,
            selected: selectedTab === TaskTabs.Deposits,
            tag: depositIncompleteCount.toString(),
          },
        ]
      : []),
    {
      id: TaskTabs.Other,
      label: TaskTabs.Other,
      selected: selectedTab === TaskTabs.Other,
      tag: otherIncompleteCount.toString(),
    },
  ]

  const firstDisplayTask = displayTasks[0]
  const stageTransitionWarning =
    selectedWorkflowStage === TaskCategoryStage.Conditions &&
    firstDisplayTask !== undefined &&
    isLastIncompleteTaskInStage({
      task: firstDisplayTask,
      tasks,
      stage: TaskCategoryStage.Conditions,
    })

  const renderTask = (task: AutopilotTaskDto) => (
    <TaskCollapsible
      key={task.id}
      task={task}
      stageTransitionWarning={stageTransitionWarning}
      assigneeOptions={assigneeOptions}
      assignTask={assignTaskToUser}
      toggleTaskSteps={toggleTaskSteps}
      renderTaskContent={({ task: currentTask, children }) => (
        <TaskBlocker
          task={currentTask}
          onTasksInvalidated={() => invalidateTasks(campaignId, contractId)}
        >
          {children}
        </TaskBlocker>
      )}
    />
  )

  return (
    <AgxSpacer fill>
      <AgxSpacer direction="vertical" fill gap="spacing-2xl">
        <AgxSpacer
          id="subPageHeadingContainer"
          direction="vertical"
          fill
          gap="sm"
        >
          <AgxSpacer
            direction="horizontal"
            fill
            contentJustification="space-between"
            contentAlignment="center"
          >
            <AgxTab
              onChange={(tab) =>
                navigate({ hash: `tab=${tab.id}` }, { replace: true })
              }
              options={tabOptions}
            />
            {selectedTab === TaskTabs.Tasks && (
              <AgxSpacer
                fill
                direction="horizontal"
                contentJustification="end"
                contentAlignment="center"
              >
                {workflowBadges.map((badge, index) => {
                  const isClickable =
                    badge.status !== WorkflowStageIndicatorStatus.LOCKED &&
                    badge.stage !== undefined
                  return (
                    <Fragment key={`${badge.stage ?? 'none'}-${badge.name}`}>
                      <AgxBadge
                        type="colourfill"
                        colour={badge.color}
                        size="md"
                        text={badge.name}
                        variant={isClickable ? 'clickable' : 'normal'}
                        onClick={
                          isClickable
                            ? () => handleBadgeClick(badge)
                            : undefined
                        }
                        hoverBackgroundColor={
                          isClickable &&
                          badge.status ===
                            WorkflowStageIndicatorStatus.COMPLETED
                            ? 'var(--bg-progress)'
                            : undefined
                        }
                      />
                      {index < workflowBadges.length - 1 && (
                        <Icon_Chevron
                          width={18}
                          height={18}
                          direction={'right'}
                        />
                      )}
                    </Fragment>
                  )
                })}
              </AgxSpacer>
            )}
          </AgxSpacer>
          <AgxDivider variant="horizontal" pushMargin="spacing-3xl" />
        </AgxSpacer>
        {selectedTab === TaskTabs.Tasks &&
          allTasksCompleted &&
          !isViewingCompletedStage && (
            <AgxSpacer
              direction="horizontal"
              fill
              contentJustification="center"
            >
              <Panel
                padding={{
                  bottom: 'xl',
                }}
              >
                <AgxSpacer
                  fill
                  contentJustification="center"
                  gap="small"
                  contentAlignment="center"
                >
                  <AgxAnimatedToggleLoader
                    className="tasksCompletedToggleLoader"
                    state={AgxAnimatedToggleLoaderState.Complete}
                  />
                  <AgxTypography
                    id="allTasksCompleted"
                    type="body"
                    size="medium"
                  >
                    All {primaryWorkflowType.toLowerCase()} tasks are completed
                  </AgxTypography>
                </AgxSpacer>
              </Panel>
            </AgxSpacer>
          )}

        {selectedTab === TaskTabs.Tasks &&
          (!allTasksCompleted || isViewingCompletedStage) &&
          displayTasks.map(renderTask)}

        {selectedTab === TaskTabs.Deposits && depositTasks.map(renderTask)}

        {selectedTab === TaskTabs.Other && otherTasks.map(renderTask)}

        {renderAdditionalContent?.({
          selectedTab,
          selectedWorkflowStage,
          allTasksCompleted,
          tasks,
          isViewingCompletedStage,
        })}
      </AgxSpacer>
    </AgxSpacer>
  )
}

export default Tasks
